import React from "react";

import {
  ArrowUpDown,
  ChevronRight,
  Grid3x3,
  ScatterChart,
  Search,
  SquarePlay,
} from "lucide-react";

import { ACCENT, PartialStatus, StatusBadge } from "./Chrome";
import { useAppSelector } from "../../../Store/Hooks";
import type { XFactorPortfolioAccount } from "../../../Types/XFactorViewsRendering";
import { DEFAULT_HEALTH_SCORE_THRESHOLDS } from "../../../Utils/HealthThresholds";
import { BAND_FILL, bandOf, formatDate } from "../lib/derive";

/**
 * Score cells tint the *background* only and leave the number near-black at
 * regular weight — measured off xfactor, where an 88 and a 57 differ solely by
 * cell fill. Colouring the digit (the obvious choice) reads noticeably louder
 * than the source.
 */
const CELL_FILL_ALPHA = "14"; // ~8% hex alpha
const CELL_TEXT = "#020618";

type SortKey =
  | "account"
  | "overall"
  | "health"
  | "fit"
  | "opportunity"
  | "risk";

const SCORE_COLUMNS: Array<{
  key: SortKey;
  label: string;
}> = [
  { key: "overall", label: "Overall" },
  { key: "health", label: "Health" },
  { key: "fit", label: "Fit" },
  { key: "opportunity", label: "Oppty" },
  { key: "risk", label: "Risk" },
];

/**
 * The accounts portfolio table.
 *
 * Only the Public Data score group: xfactor's KPI Metrics group is driven by
 * Stage 8 resultsets, which have no endpoint on the bearer admin API and so
 * cannot be read from EB at all.
 */
const AccountsPortfolio: React.FC<{
  accounts: XFactorPortfolioAccount[];
  loading: boolean;
  onOpenAccount: (account: XFactorPortfolioAccount) => void;
  onOpenBubble: () => void;
  onOpenRo: () => void;
}> = ({ accounts, loading, onOpenAccount, onOpenBubble, onOpenRo }) => {
  const bhThresholds = useAppSelector(
    state =>
      state.vendorAdmin.healthScoreThresholds?.business_health ??
      DEFAULT_HEALTH_SCORE_THRESHOLDS.business_health
  );
  const [query, setQuery] = React.useState("");
  const [sort, setSort] = React.useState<{ key: SortKey; dir: "asc" | "desc" }>(
    {
      key: "overall",
      dir: "desc",
    }
  );

  const rows = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? accounts.filter(a => (a.account_name || "").toLowerCase().includes(q))
      : accounts;

    // Account-name tie-breaker keeps tied rows from reordering between renders.
    return [...filtered].sort((a, b) => {
      const name = (x: XFactorPortfolioAccount) =>
        (x.account_name || "").toLowerCase();
      if (sort.key === "account") {
        const cmp = name(a).localeCompare(name(b));
        return sort.dir === "asc" ? cmp : -cmp;
      }
      const va = a.scores[sort.key];
      const vb = b.scores[sort.key];
      if (va == null && vb == null) return name(a).localeCompare(name(b));
      if (va == null) return 1;
      if (vb == null) return -1;
      if (va !== vb) return sort.dir === "asc" ? va - vb : vb - va;
      return name(a).localeCompare(name(b));
    });
  }, [accounts, query, sort]);

  const toggleSort = (key: SortKey) =>
    setSort(s =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "account" ? "asc" : "desc" }
    );

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            Accounts Portfolio
          </h2>
          <p className="text-xs text-gray-500">
            {rows.length} of {accounts.length} account
            {accounts.length === 1 ? "" : "s"} · Latest account scores
          </p>
        </div>
        <div className="relative">
          <Search
            size={14}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            aria-hidden
          />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search accounts…"
            className="w-56 rounded-md border border-gray-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-[#ad46ff]"
          />
        </div>
      </div>

      <p className="mb-2 flex flex-wrap items-center gap-1 text-xs text-gray-500">
        Mouse over scores to see brief synopsis. Click on action
        <SquarePlay size={13} style={{ color: ACCENT }} aria-hidden />
        to go to Situation Card for an account. Click on
        <Grid3x3 size={12} style={{ color: ACCENT }} aria-hidden />
        to see a bubble chart. Click on
        <ScatterChart size={12} style={{ color: ACCENT }} aria-hidden />
        to see Risk &amp; Oppty Chart.
      </p>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full min-w-[860px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50/70">
              <th className="px-4 py-2 text-left" />
              <th
                colSpan={SCORE_COLUMNS.length}
                className="border-l border-gray-200 px-4 py-2"
              >
                <div className="flex items-center justify-center gap-3">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Business Health
                  </span>
                  <button
                    type="button"
                    onClick={onOpenBubble}
                    className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-[#ad46ff] transition-colors hover:bg-purple-50"
                  >
                    <Grid3x3 size={12} aria-hidden /> Bubble
                  </button>
                  <button
                    type="button"
                    onClick={onOpenRo}
                    className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-[#ad46ff] transition-colors hover:bg-purple-50"
                  >
                    <ScatterChart size={12} aria-hidden /> R-O
                  </button>
                </div>
              </th>
              <th className="border-l border-gray-200 px-4 py-2" />
            </tr>
            <tr className="border-b border-gray-200 text-[11px] uppercase tracking-wide text-gray-500">
              <th className="px-4 py-2 text-left">
                <button
                  type="button"
                  onClick={() => toggleSort("account")}
                  className="inline-flex items-center gap-1 hover:text-gray-900"
                >
                  Account <ArrowUpDown size={11} aria-hidden />
                </button>
              </th>
              {SCORE_COLUMNS.map(c => (
                <th key={c.key} className="px-3 py-2 text-center">
                  <button
                    type="button"
                    onClick={() => toggleSort(c.key)}
                    className="inline-flex items-center gap-1 hover:text-gray-900"
                  >
                    {c.label}
                    {sort.key === c.key ? (
                      <span aria-hidden>{sort.dir === "asc" ? "↑" : "↓"}</span>
                    ) : (
                      <ArrowUpDown size={11} aria-hidden />
                    )}
                  </button>
                </th>
              ))}
              <th className="px-4 py-2 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && !rows.length ? (
              <tr>
                <td
                  colSpan={SCORE_COLUMNS.length + 2}
                  className="px-4 py-10 text-center text-sm text-gray-400"
                >
                  Loading account portfolio…
                </td>
              </tr>
            ) : null}

            {!loading && !rows.length ? (
              <tr>
                <td
                  colSpan={SCORE_COLUMNS.length + 2}
                  className="px-4 py-10 text-center text-sm text-gray-400"
                >
                  No accounts with briefings in this organization.
                </td>
              </tr>
            ) : null}

            {rows.map(a => (
              <tr
                key={a.digest_id}
                className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/60"
              >
                <td className="px-4 py-3">
                  {/* The name carries its own chevron into the Situation Card —
                      xfactor offers this alongside the Action button. */}
                  <button
                    type="button"
                    onClick={() => onOpenAccount(a)}
                    className="group flex items-center gap-1 text-left font-medium text-gray-900"
                  >
                    {a.account_name || "Unknown account"}
                    <ChevronRight
                      size={13}
                      className="text-gray-400 transition-colors group-hover:text-[#ad46ff]"
                      aria-hidden
                    />
                  </button>
                  <div className="mt-0.5 flex flex-wrap items-center text-[11px] text-gray-500">
                    <span>Public: {formatDate(a.digest_date) || "—"}</span>
                    {a.is_stale ? <StatusBadge kind="stale" /> : null}
                    {a.is_partial ? (
                      <PartialStatus
                        missingSectionIds={a.missing_section_ids}
                      />
                    ) : null}
                    {a.has_payload === false ? (
                      <StatusBadge kind="missing" />
                    ) : null}
                  </div>
                </td>
                {SCORE_COLUMNS.map(c => {
                  const value = a.scores[c.key as keyof typeof a.scores];
                  const band = bandOf(value, bhThresholds);
                  return (
                    <td
                      key={c.key}
                      className="px-3 py-3 text-center"
                      style={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: value == null ? "#cbd5e1" : CELL_TEXT,
                        backgroundColor:
                          value == null
                            ? undefined
                            : `${BAND_FILL[band]}${CELL_FILL_ALPHA}`,
                      }}
                    >
                      {value == null ? "—" : Math.round(value)}
                    </td>
                  );
                })}
                <td className="px-4 py-3 text-center">
                  <button
                    type="button"
                    onClick={() => onOpenAccount(a)}
                    aria-label={`Open ${a.account_name || "account"} situation card`}
                    title="Open Situation Card"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-[#ad46ff]/10"
                    style={{ color: ACCENT }}
                  >
                    <SquarePlay size={22} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AccountsPortfolio;
